import React, { useState, useEffect } from 'react';
import { X, RefreshCw, Bell, ArrowDown, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'msp_seen_update_deals_onboarding';

/**
 * One-time onboarding modal explaining the "New deals found" live-update banner.
 * Shows automatically the first time `hasNewDeals` becomes true.
 * Tracks dismissal in localStorage so it never shows again.
 *
 * Usage:
 *   <UpdateDealsOnboardingModal
 *       show={hasNewDeals}
 *       scanType="live"        // 'live' | 'reverse' | 'storefront' | 'wholesale' (default: 'live')
 *   />
 */
const UpdateDealsOnboardingModal = ({ show, scanType = 'live' }) => {
    const [visible, setVisible] = useState(false);
    const [step, setStep] = useState(0);
    const [animateIn, setAnimateIn] = useState(false);

    useEffect(() => {
        if (!show) return;

        // Check localStorage - if already seen, never show
        try {
            if (localStorage.getItem(STORAGE_KEY)) return;
        } catch {
            return; // If localStorage unavailable, skip
        }

        setVisible(true);
        // Trigger entrance animation on next frame
        requestAnimationFrame(() => setAnimateIn(true));
    }, [show]);

    const handleDismiss = () => {
        setAnimateIn(false);
        setTimeout(() => {
            setVisible(false);
            try {
                localStorage.setItem(STORAGE_KEY, 'true');
            } catch { /* noop */ }
        }, 300);
    };

    if (!visible) return null;

    const scanLabels = {
        live: { name: 'Live Scan', verb: 'scanning supplier catalogs' },
        reverse: { name: 'Reverse Scan', verb: 'matching products across marketplaces' },
        storefront: { name: 'Storefront Scan', verb: 'analyzing storefront listings' },
        wholesale: { name: 'Wholesale Scan', verb: 'scanning wholesale catalog products' },
    };

    const label = scanLabels[scanType] || scanLabels.live;

    const steps = [
        {
            icon: <RefreshCw className="text-teal-400" size={32} />,
            title: `${label.name} in Progress`,
            description: `While your scan is running, we keep ${label.verb} in real-time. New deals can appear at any moment!`,
            illustration: <ScanningIllustration label={label} />,
        },
        {
            icon: <Bell className="text-amber-400" size={32} />,
            title: 'New Deals Notification',
            description: 'When new deals are found, a banner will appear at the bottom of your screen showing how many new deals were discovered.',
            illustration: <BannerIllustration />,
        },
        {
            icon: <ArrowDown className="text-green-400" size={32} />,
            title: 'Tap "Update" to Load Them',
            description: 'Click the Update button on the banner to add the new deals to your list. You can also dismiss it and keep browsing - your deals won\'t be lost.',
            illustration: <UpdateIllustration />,
        },
    ];

    const currentStep = steps[step];
    const isLast = step === steps.length - 1;

    return (
        <div
            className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${animateIn ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'
                }`}
            onClick={handleDismiss}
        >
            <div
                className={`relative bg-gray-900 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl transition-all duration-300 ${animateIn ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={handleDismiss}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-300 transition z-10"
                >
                    <X size={20} />
                </button>

                {/* Glowing top accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500" />

                {/* Content */}
                <div className="p-6 pt-8">
                    {/* Step indicator */}
                    <div className="flex items-center justify-center gap-2 mb-6">
                        {steps.map((_, i) => (
                            <div
                                key={i}
                                className={`h-1.5 rounded-full transition-all duration-300 ${i === step
                                    ? 'w-8 bg-teal-500'
                                    : i < step
                                        ? 'w-4 bg-teal-700'
                                        : 'w-4 bg-gray-700'
                                    }`}
                            />
                        ))}
                    </div>

                    {/* Illustration area */}
                    <div className="h-44 flex items-center justify-center mb-6 relative overflow-hidden rounded-xl bg-gray-800/50 border border-gray-800">
                        {currentStep.illustration}
                    </div>

                    {/* Icon + Text */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-800 mb-3">
                            {currentStep.icon}
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">
                            {currentStep.title}
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                            {currentStep.description}
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3">
                        {step > 0 && (
                            <button
                                onClick={() => setStep(s => s - 1)}
                                className="flex-1 py-3 rounded-xl font-semibold text-gray-400 bg-gray-800 hover:bg-gray-700 transition text-sm"
                            >
                                Back
                            </button>
                        )}
                        <button
                            onClick={() => {
                                if (isLast) {
                                    handleDismiss();
                                } else {
                                    setStep(s => s + 1);
                                }
                            }}
                            className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-400 hover:to-blue-500 transition text-sm flex items-center justify-center gap-2"
                        >
                            {isLast ? (
                                <>
                                    <Sparkles size={16} />
                                    Got it!
                                </>
                            ) : (
                                'Next →'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Inline Illustrations ────────────────────────────────────────────────────

const ScanningIllustration = ({ label }) => (
    <div className="flex flex-col items-center gap-3 w-full p-4">
        {/* Animated scanning bars */}
        <div className="flex items-end gap-2 h-16">
            {[0, 1, 2, 3, 4, 5, 6].map(i => (
                <div
                    key={i}
                    className="w-4 bg-teal-500/60 rounded-t"
                    style={{
                        height: `${20 + Math.sin(i * 0.9) * 30}%`,
                        animation: `scanPulse 1.5s ease-in-out ${i * 0.15}s infinite alternate`,
                    }}
                />
            ))}
        </div>
        {/* Simulated product cards being found */}
        <div className="flex gap-2">
            {[0, 1, 2].map(i => (
                <div
                    key={i}
                    className="w-16 h-10 rounded-lg border border-gray-700 bg-gray-800 flex items-center justify-center"
                    style={{ animation: `fadeSlideUp 0.6s ease-out ${0.8 + i * 0.3}s both` }}
                >
                    <span className="text-xs text-teal-400 font-mono">$</span>
                </div>
            ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">{label.verb.charAt(0).toUpperCase() + label.verb.slice(1)}…</p>
        <style>{`
            @keyframes scanPulse {
                0% { height: 20%; opacity: 0.4; }
                100% { height: 90%; opacity: 1; }
            }
            @keyframes fadeSlideUp {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
            }
        `}</style>
    </div>
);

const BannerIllustration = () => (
    <div className="flex flex-col items-center justify-center w-full p-4 gap-4">
        {/* Mock deal rows (background) */}
        <div className="w-full space-y-2 opacity-40">
            {[0, 1, 2].map(i => (
                <div key={i} className="h-6 bg-gray-700 rounded w-full" />
            ))}
        </div>
        {/* The banner itself */}
        <div
            className="flex items-center gap-3 bg-gray-950 border border-gray-700 px-5 py-2.5 rounded-full shadow-lg shadow-teal-500/10"
            style={{ animation: 'bannerBounce 2s ease-in-out infinite' }}
        >
            <span className="text-sm">🎉</span>
            <span className="text-white text-sm font-medium">3 new deals found!</span>
            <span className="bg-teal-500 text-white text-xs px-3 py-1 rounded-full font-semibold">Update</span>
            <span className="text-gray-500 text-xs cursor-pointer">✕</span>
        </div>
        <style>{`
            @keyframes bannerBounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-4px); }
            }
        `}</style>
    </div>
);

const UpdateIllustration = () => (
    <div className="flex flex-col items-center justify-center w-full p-4 gap-3">
        {/* Before: old deals */}
        <div className="w-full space-y-1.5">
            {[0, 1].map(i => (
                <div key={i} className="h-5 bg-gray-700 rounded w-full" />
            ))}
        </div>
        {/* Arrow + new deals sliding in */}
        <div className="flex items-center gap-2 text-teal-400">
            <div className="h-px w-8 bg-teal-500/50" />
            <ArrowDown size={16} style={{ animation: 'arrowPulse 1s ease-in-out infinite' }} />
            <div className="h-px w-8 bg-teal-500/50" />
        </div>
        {/* New deals with glow */}
        <div className="w-full space-y-1.5">
            {[0, 1, 2].map(i => (
                <div
                    key={i}
                    className="h-5 rounded w-full border"
                    style={{
                        background: 'linear-gradient(90deg, rgba(20,184,166,0.15), rgba(37,99,235,0.15))',
                        borderColor: 'rgba(20,184,166,0.3)',
                        animation: `newDealSlide 0.5s ease-out ${i * 0.15}s both`,
                    }}
                />
            ))}
        </div>
        <p className="text-xs text-gray-500">New deals are added to the top of your list</p>
        <style>{`
            @keyframes arrowPulse {
                0%, 100% { transform: translateY(0); opacity: 0.6; }
                50% { transform: translateY(3px); opacity: 1; }
            }
            @keyframes newDealSlide {
                from { opacity: 0; transform: translateX(-10px); }
                to { opacity: 1; transform: translateX(0); }
            }
        `}</style>
    </div>
);

export default UpdateDealsOnboardingModal;